import { useCallback, useRef } from 'react';

/**
 * Identyfikator operacji klienta (docs/adr/0003-ksztalt-outboxu.md).
 *
 * Powstaje raz na *próbę zapisu*, a nie raz na wysłane żądanie: gdy sieć zgubi
 * odpowiedź i użytkownik kliknie „Zapisz" jeszcze raz, serwer dostaje ten sam
 * identyfikator i zwraca wynik pierwszej próby zamiast zakładać drugą kartę.
 * Dopiero udany zapis kasuje identyfikator.
 *
 * Ten sam mechanizm obsłuży kolejkę wysyłkową, gdy powstanie — wtedy identyfikator
 * będzie leżał w IndexedDB razem z operacją, a nie w pamięci komponentu.
 */

function losowyIdentyfikator(): string {
  // `crypto.randomUUID` istnieje tylko w bezpiecznym kontekście. Pierwszy test
  // u klienta bywa po http w sieci lokalnej — wtedy trzeba mieć czym go zastąpić.
  const kryptografia = globalThis.crypto as Crypto | undefined;

  if (typeof kryptografia?.randomUUID === 'function') return kryptografia.randomUUID();

  const bajty = new Uint8Array(16);
  if (typeof kryptografia?.getRandomValues === 'function') {
    kryptografia.getRandomValues(bajty);
  } else {
    // Ostateczność: identyfikator ma być niepowtarzalny, nie nieodgadywalny —
    // służy do rozpoznania powtórki własnego zapisu, nie do zabezpieczenia czegokolwiek.
    for (let i = 0; i < bajty.length; i += 1) bajty[i] = Math.floor(Math.random() * 256);
  }

  bajty[6] = (bajty[6]! & 0x0f) | 0x40;
  bajty[8] = (bajty[8]! & 0x3f) | 0x80;

  const hex = [...bajty].map((bajt) => bajt.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export interface IdOperacji {
  /** Identyfikator bieżącej próby — ten sam, dopóki zapis się nie powiedzie. */
  biezacy: () => string;
  /** Zapis się udał: kolejny zapis to nowa operacja. */
  zakoncz: () => void;
}

export function useIdOperacji(): IdOperacji {
  const identyfikator = useRef<string | null>(null);

  const biezacy = useCallback((): string => {
    identyfikator.current ??= losowyIdentyfikator();
    return identyfikator.current;
  }, []);

  const zakoncz = useCallback((): void => {
    identyfikator.current = null;
  }, []);

  return { biezacy, zakoncz };
}
